import { useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { 
  logUserAction, 
  logBusinessEvent, 
  measurePerformance,
  UserAction 
} from '../lib/monitoring'

export const useMonitoring = () => {
  const { user } = useAuth()

  // Log user actions with automatic user context
  const trackAction = useCallback((
    action: string,
    category: UserAction['category'],
    details?: Record<string, any>
  ) => {
    logUserAction({
      action,
      category,
      details,
      userId: user?.id,
      timestamp: Date.now()
    })
  }, [user?.id])

  // Track specific business events
  const trackBusinessEvent = useCallback((
    type: 'booking_created' | 'payment_processed' | 'invoice_sent' | 'member_registered' | 'facility_conflict',
    data: Record<string, any>
  ) => {
    logBusinessEvent({
      type,
      userId: user?.id,
      data
    })
  }, [user?.id])

  // Measure performance of operations
  const measureOperation = useCallback(async <T>(
    name: string,
    operation: () => Promise<T>,
    category: string = 'user_operation'
  ): Promise<T> => {
    return measurePerformance(name, operation, category)
  }, [])

  // Track page views
  const trackPageView = useCallback((pageName: string, metadata?: Record<string, any>) => {
    trackAction('page_view', 'navigation', {
      page: pageName,
      ...metadata
    })
  }, [trackAction])

  // Track form submissions
  const trackFormSubmission = useCallback((
    formName: string, 
    success: boolean, 
    errors?: string[]
  ) => {
    trackAction('form_submit', 'navigation', {
      form: formName,
      success,
      errors,
      timestamp: Date.now()
    })
  }, [trackAction])

  // Track authentication events
  const trackAuth = useCallback((
    action: 'login' | 'logout' | 'signup' | 'password_reset',
    success: boolean,
    method?: string
  ) => {
    trackAction(action, 'auth', {
      success,
      method,
      timestamp: Date.now()
    })
  }, [trackAction])

  // Track booking operations
  const trackBooking = useCallback((
    action: 'create' | 'update' | 'cancel' | 'conflict_detected',
    bookingData?: Record<string, any>
  ) => {
    trackAction(`booking_${action}`, 'booking', bookingData)
    
    if (action === 'create' && bookingData) {
      trackBusinessEvent('booking_created', bookingData)
    }
    
    if (action === 'conflict_detected') {
      trackBusinessEvent('facility_conflict', bookingData || {})
    }
  }, [trackAction, trackBusinessEvent])

  // Track payment operations
  const trackPayment = useCallback((
    action: 'initiated' | 'completed' | 'failed' | 'refunded',
    paymentData?: Record<string, any>
  ) => {
    trackAction(`payment_${action}`, 'payment', paymentData)
    
    if (action === 'completed' && paymentData) {
      trackBusinessEvent('payment_processed', paymentData)
    }
  }, [trackAction, trackBusinessEvent])

  // Track member operations
  const trackMember = useCallback((
    action: 'create' | 'update' | 'deactivate' | 'search',
    memberData?: Record<string, any>
  ) => {
    trackAction(`member_${action}`, 'member', memberData)
    
    if (action === 'create' && memberData) {
      trackBusinessEvent('member_registered', memberData)
    }
  }, [trackAction, trackBusinessEvent])

  // Track invoice operations
  const trackInvoice = useCallback((
    action: 'create' | 'send' | 'pay' | 'void' | 'download',
    invoiceData?: Record<string, any>
  ) => {
    trackAction(`invoice_${action}`, 'invoice', invoiceData)
    
    if (action === 'send' && invoiceData) {
      trackBusinessEvent('invoice_sent', invoiceData)
    }
  }, [trackAction, trackBusinessEvent])

  // Track communication events
  const trackCommunication = useCallback((
    action: 'email_sent' | 'sms_sent' | 'template_used' | 'bulk_message',
    communicationData?: Record<string, any>
  ) => {
    trackAction(`communication_${action}`, 'communication', communicationData)
  }, [trackAction])

  // Track errors in user flows
  const trackError = useCallback((
    operation: string,
    errorMessage: string,
    context?: Record<string, any>
  ) => {
    trackAction('error_encountered', 'navigation', {
      operation,
      error: errorMessage,
      context,
      timestamp: Date.now()
    })
  }, [trackAction])

  return {
    // General tracking
    trackAction,
    trackPageView,
    trackFormSubmission,
    trackError,
    measureOperation,
    
    // Specific domain tracking
    trackAuth,
    trackBooking,
    trackPayment,
    trackMember,
    trackInvoice,
    trackCommunication,
    
    // Business events
    trackBusinessEvent
  }
}

// Custom hook for performance monitoring
export const usePerformanceMonitoring = () => {
  const { measureOperation } = useMonitoring()

  const monitorQuery = useCallback(async <T>(
    queryName: string,
    queryFn: () => Promise<T>
  ): Promise<T> => {
    return measureOperation(`query_${queryName}`, queryFn, 'database')
  }, [measureOperation])

  const monitorMutation = useCallback(async <T>(
    mutationName: string,
    mutationFn: () => Promise<T>
  ): Promise<T> => {
    return measureOperation(`mutation_${mutationName}`, mutationFn, 'database')
  }, [measureOperation])

  const monitorApiCall = useCallback(async <T>(
    apiEndpoint: string,
    apiFn: () => Promise<T>
  ): Promise<T> => {
    return measureOperation(`api_${apiEndpoint}`, apiFn, 'api')
  }, [measureOperation])

  return {
    monitorQuery,
    monitorMutation,
    monitorApiCall
  }
}